import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import FieldError from 'client/widget-components/components/FieldError';
import { Customization } from 'client/widget-components/ecom/stores/types';
import { mergeResponsiveStyles } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { ChangeEventHandler, FC, memo, useCallback } from 'react';
import { ProductCustomizationsStyles } from '../../types';
import BaseProductCustomizationLabel from '../BaseProductCustomizationLabel';
import CheckboxCustomizationChoices from './CheckboxCustomizationChoices';

interface CheckboxCustomizationProps extends Customization {
    styles?: ProductCustomizationsStyles;
    value?: string[];
    error?: string | null;
    onChange: (fieldId: string, value: string[]) => void;
    validateField: (fieldId: string) => void;
}

const CheckboxCustomization: FC<CheckboxCustomizationProps> = ({
    id,
    label,
    mandatory = false,
    choices,
    error,
    limitations,
    value = [],
    styles,
    onChange,
}) => {
    const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            const { checked, name } = e.target;

            if (checked) {
                onChange(id, [...value, name]);
            } else {
                onChange(
                    id,
                    value.filter((v) => v !== name)
                );
            }
        },
        [onChange, id, value]
    );

    return (
        <div data-auto='product-customizations-checkbox'>
            <BaseProductCustomizationLabel
                label={label}
                required={mandatory}
                styles={mergeResponsiveStyles([
                    labelDefaultStyles,
                    styles?.inputLabel,
                ])}
                dataAuto='product-customizations-checkbox-label'
            />
            <CheckboxCustomizationChoices
                choices={choices}
                limitations={limitations}
                value={value}
                styles={{
                    checkboxesContainer: styles?.checkboxesContainer,
                    checkboxChecked: styles?.checkboxChecked,
                    checkbox: styles?.checkbox,
                    checkboxLabel: styles?.checkboxLabel,
                }}
                onChange={handleChange}
            />
            {!!error && (
                <FieldError error={error} styles={fieldErrorDefaultStyles} />
            )}
        </div>
    );
};

const labelDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        marginBlockEnd: '8px',
    },
};

export default memo(CheckboxCustomization);

const fieldErrorDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        marginBlockStart: '8px',
    },
};
